import { KaboodleToaster } from "components/common";
import { memo, useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  useCreateNoteMutation,
  useUpdateNoteMutation,
} from "services/notes-api";
import type { NoteType, UserType } from "types/DataTypes";

import type { NotesEntity } from "./notesAdapter";

type NotesFormProps = {
  entity: NotesEntity;
  editingNote?: NoteType | null;
  onEditComplete?: () => void;
};

type FormData = {
  text: string;
  mentionee?: number;
};

const NotesForm = ({ entity, editingNote, onEditComplete }: NotesFormProps) => {
  const [createNote, { isLoading: isCreating }] = useCreateNoteMutation();
  const [updateNote, { isLoading: isUpdating }] = useUpdateNoteMutation();
  const [availableUsers] = useState<UserType[]>([]); // TODO: implement user fetching if needed

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    mode: "onChange",
    defaultValues: {
      text: "",
      mentionee: undefined,
    },
  });

  const MAX_NOTE_LENGTH = 3000;

  const watchedText = watch("text");

  // populate form when editing
  useEffect(() => {
    if (editingNote) {
      setValue("text", editingNote.text);
      setValue("mentionee", editingNote.mentionee);
    } else {
      reset({
        text: "",
        mentionee: undefined,
      });
    }
  }, [editingNote, setValue, reset]);

  const onSubmit = useCallback(
    async (data: FormData) => {
      try {
        if (editingNote) {
          // update existing note - preserve original association type
          // if it has lien, keep lien; if it has lien_file, keep lien_file
          await updateNote({
            id: editingNote.id,
            data: {
              text: data.text,
              ...(entity.fileId && { file: entity.fileId }),
              service: entity.serviceId,
              // preserve legacy lien association if it exists
              ...(editingNote.lien && { lien: editingNote.lien }),
              // preserve lien_file association if it exists
              ...(editingNote.lien_file && {
                lien_file: editingNote.lien_file,
              }),
              claim: entity.claimId,
              mentionee: data.mentionee || null,
            },
          }).unwrap();

          KaboodleToaster.success("Note updated successfully");
          if (onEditComplete) {
            onEditComplete();
          }
        } else {
          // create new note - use lien_file for new notes
          await createNote({
            data: {
              text: data.text,
              ...(entity.fileId && { file: entity.fileId }),
              service: entity.serviceId,
              lien_file: entity.lienFileId, // new: use lien_file instead of lien
              claim: entity.claimId,
              mentionee: data.mentionee,
            },
          }).unwrap();

          KaboodleToaster.success("Note created successfully");
          reset({
            text: "",
            mentionee: undefined,
          });
        }
      } catch (error) {
        console.error("Error saving note:", error);
        const errorMessage = editingNote
          ? "Failed to update note"
          : "Failed to create note";
        KaboodleToaster.error(`${errorMessage}. Please try again.`);
      }
    },
    [createNote, updateNote, editingNote, entity, reset, onEditComplete],
  );

  const handleCancel = useCallback(() => {
    if (editingNote && onEditComplete) {
      onEditComplete();
    } else {
      reset({
        text: "",
        mentionee: undefined,
      });
    }
  }, [editingNote, onEditComplete, reset]);

  // check if the text content is empty
  const isTextEmpty = useCallback(() => {
    return !watchedText || watchedText.trim() === "";
  }, [watchedText]);

  const isSubmitDisabled = isTextEmpty() || isCreating || isUpdating;

  return (
    <div className="NotesForm__wrapper">
      {editingNote && (
        <div className="NotesForm__edit-header">
          <h4>Edit Note</h4>
        </div>
      )}
      <form onSubmit={handleSubmit(onSubmit)} className="NotesForm">
        <div className="NotesForm__field">
          <textarea
            {...register("text", { required: "Note content is required" })}
            className="NotesForm__textarea"
            rows={editingNote ? 6 : 3}
            placeholder={editingNote ? "Edit your note..." : "Add a note..."}
            maxLength={MAX_NOTE_LENGTH}
          />
          {errors.text && (
            <span className="NotesForm__error">{errors.text.message}</span>
          )}
        </div>

        {availableUsers.length > 0 && (
          <div className="NotesForm__field">
            <label className="NotesForm__label">Mention User (Optional)</label>
            <select {...register("mentionee")} className="NotesForm__select">
              <option value="">Select a user to mention...</option>
              {availableUsers.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.first_name} {user.last_name} ({user.email})
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="NotesForm__actions">
          <button
            type="submit"
            className="button button--primary"
            disabled={isSubmitDisabled}
          >
            {isCreating || isUpdating
              ? "Saving..."
              : editingNote
                ? "Update Note"
                : "Create Note"}
          </button>

          {editingNote && (
            <button
              type="button"
              className="button button--secondary"
              onClick={handleCancel}
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default memo(NotesForm);
